import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Portfolio Cash Flow page.
//
// Originally just a form-submit helper for the As Of / grain / horizon
// controls. Portfolio plan items B + C added:
//   - the editable By Project grid (cells write through
//     /portfolio/cash_flow/cells, same semantics as the project-scope
//     grid: bucket-start keys, blank/0 clears, Excel block paste)
//   - the AI import modal (upload → review with per-project rate →
//     apply through the same cells endpoint)
// Grid mechanics mirror cash_flow_controller.js on purpose — same keys,
// same visual states — so the two grids feel like one feature.
export default class extends Controller {
  static targets = [
    "gridRow", "gridCell", "rowTotal", "columnTotal", "grandTotal", "editButton",
    "importModal", "importDropzone", "importDropArea", "importFileInput",
    "importLoading", "importReview", "importRows", "importSummary",
    "importError", "importApplyButton",
    "importOverrideCheckbox",
    "importStandaloneCheckbox", "standaloneNewRowInput",
    "yearSelect", "expandShell", "expandScroll"
  ]

  static values = {
    cellsUrl: String,
    importUrl: String,
    standaloneApplyUrl: String,
    standaloneCellsUrl: String,
    standaloneRowsUrl: String,
    standaloneToggleUrl: String,
    standalone: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editMode = false
    this._showPendingFlash()
  }

  // ── Edit mode (grid opens read-only) ───────────────────────────────

  toggleEdit(event) {
    event?.preventDefault()
    this._editMode = !this._editMode

    this.gridCellTargets.forEach(cell => {
      cell.readOnly = !this._editMode
      cell.classList.toggle("cursor-default", !this._editMode)
      cell.classList.toggle("cursor-text", this._editMode)
      cell.classList.toggle("focus:ring-1", this._editMode)
      cell.classList.toggle("focus:ring-brand", this._editMode)
      cell.classList.toggle("focus:bg-white", this._editMode)
      // Darker, non-italic numbers while editing — the light gray
      // forecast styling is too faint to type against.
      cell.style.color = this._editMode ? "#111827" : ""
      cell.style.fontStyle = this._editMode ? "normal" : ""
    })

    if (this.hasEditButtonTarget) {
      const btn = this.editButtonTarget
      btn.classList.toggle("bg-brand", this._editMode)
      btn.classList.toggle("text-white", this._editMode)
      btn.classList.toggle("hover:bg-brand-dark", this._editMode)
      btn.classList.toggle("bg-gray-200", !this._editMode)
      btn.classList.toggle("hover:bg-gray-300", !this._editMode)
      btn.classList.toggle("text-gray-600", !this._editMode)
      const label = btn.querySelector("[data-edit-label]")
      if (label) label.textContent = this._editMode ? "Done" : "Edit"
    }

    if (this._editMode) this._track("Portfolio Cash Flow Edit Mode Entered", {})
  }

  submit(event) {
    const form = event.currentTarget.closest("form")
    if (form) form.requestSubmit()
  }

  // ── Year filter (Luma 2026-08-26) ──────────────────────────────────
  // Just a lens on the existing as_of + horizon params: a year means
  // "Jan of that year, 12 months"; All returns to the rolling default.
  yearSelected(event) {
    const form = event.currentTarget.closest("form")
    if (!form) return
    const asOf = form.querySelector('input[name="as_of"]')
    const horizon = form.querySelector('input[name="horizon_months"]')
    const year = parseInt(event.currentTarget.value, 10)

    if (Number.isFinite(year)) {
      if (asOf) asOf.value = `${year}-01-01`
      if (horizon) horizon.value = "12"
      this._track("Portfolio Cash Flow Year Filtered", { year })
    } else {
      if (asOf) asOf.value = new Date().toISOString().slice(0, 10)
      if (horizon) horizon.value = "36"
    }
    form.requestSubmit()
  }

  // ── Full screen (Luma 2026-08-26) ──────────────────────────────────
  // Inline styles on purpose: the expand state uses no Tailwind classes
  // the compiled CSS might be missing (the invisible-button lesson).
  toggleExpand(event) {
    event?.preventDefault()
    if (!this.hasExpandShellTarget) return
    const shell = this.expandShellTarget
    const label = shell.querySelector("[data-expand-label]")
    this._expanded = !this._expanded

    if (this._expanded) {
      Object.assign(shell.style, {
        position: "fixed", top: "0", right: "0", bottom: "0", left: "0",
        zIndex: "50", background: "#fff", padding: "12px", margin: "0", overflow: "auto"
      })
      if (this.hasExpandScrollTarget) this.expandScrollTarget.style.maxHeight = "calc(100vh - 70px)"
      document.body.style.overflow = "hidden"
      if (label) label.textContent = "Exit full screen"
      this._expandEscape = (e) => { if (e.key === "Escape") this.toggleExpand() }
      document.addEventListener("keydown", this._expandEscape)
      this._track("Portfolio Cash Flow Expanded", {})
    } else {
      shell.style.cssText = ""
      if (this.hasExpandScrollTarget) this.expandScrollTarget.style.maxHeight = ""
      document.body.style.overflow = ""
      if (label) label.textContent = "Full screen"
      if (this._expandEscape) document.removeEventListener("keydown", this._expandEscape)
    }
  }

  disconnect() {
    if (this._expandEscape) document.removeEventListener("keydown", this._expandEscape)
    document.body.style.overflow = ""
  }

  // ── Editable grid (item B) ─────────────────────────────────────────

  onCellFocus(event) {
    this._setFocusHighlight(event.currentTarget, true)
    if (event.currentTarget.readOnly) return
    event.currentTarget.select()
  }

  // Live money mask — "$" and thousands commas appear WHILE typing
  // ("1234567" reads "$1,234,567" as it's entered), with the caret kept
  // in place relative to the digits around it.
  onCellInput(event) {
    const cell = event.currentTarget
    if (cell.readOnly) return

    const before = cell.value
    const caret = cell.selectionStart ?? before.length
    const digitsBeforeCaret = before.slice(0, caret).replace(/[^\d.]/g, "").length
    const masked = this._maskMoney(before)
    if (masked === before) return

    cell.value = masked
    let pos = 0
    let seen = 0
    while (pos < masked.length && seen < digitsBeforeCaret) {
      if (/[\d.]/.test(masked[pos])) seen += 1
      pos += 1
    }
    cell.setSelectionRange(pos, pos)
  }

  _maskMoney(raw) {
    const stripped = String(raw ?? "").replace(/[^\d.]/g, "")
    if (stripped === "") return ""

    const firstDot = stripped.indexOf(".")
    let int = firstDot === -1 ? stripped : stripped.slice(0, firstDot)
    const dec = firstDot === -1 ? null : stripped.slice(firstDot + 1).replace(/\./g, "").slice(0, 2)
    int = int.replace(/^0+(?=\d)/, "")
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return "$" + withCommas + (dec === null ? "" : "." + dec)
  }

  // Tint the row + its row total + the column total while a cell is
  // focused, so the user always knows which project/month they're on.
  _setFocusHighlight(cell, on) {
    const row = cell.closest("tr")
    if (row) row.classList.toggle("bg-brand/5", on)

    const rowTotal = this.rowTotalTargets.find(t => t.dataset.projectId === cell.dataset.projectId)
    if (rowTotal) {
      rowTotal.classList.toggle("bg-brand/5", on)
      rowTotal.classList.toggle("text-brand", on)
    }

    const colTotal = this.columnTotalTargets.find(t => t.dataset.bucketIndex === cell.dataset.bucketIndex)
    if (colTotal) {
      colTotal.classList.toggle("bg-brand/5", on)
      colTotal.classList.toggle("text-brand", on)
    }
  }

  onCellKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      const cell = event.currentTarget
      cell.value = cell.dataset.original || ""
      cell.blur()
    }
  }

  async onCellCommit(event) {
    const cell = event.currentTarget
    this._setFocusHighlight(cell, false)
    const next = cell.value.trim()
    if (next === (cell.dataset.original || "").trim()) return

    const ok = await this._saveCells([cell], { source: "manual" })
    if (ok) this._track("Portfolio Cash Flow Cell Edited", { cleared: next === "" })
  }

  // Excel paste: \n rows, \t columns, filling right and down from the
  // focused cell. Read-only (line-managed) rows have no inputs, so the
  // fill silently skips them — only editable cells are touched.
  async onCellPaste(event) {
    if (!this._editMode) return
    const text = event.clipboardData?.getData("text/plain")
    if (!text || !/[\t\n]/.test(text)) return

    event.preventDefault()
    const start = event.currentTarget
    const rows = text.replace(/\r/g, "").replace(/\n+$/, "").split("\n")
    const startRowIdx = this.gridRowTargets.indexOf(start.closest("tr"))
    const startColIdx = parseInt(start.dataset.bucketIndex, 10)
    if (startRowIdx < 0 || !Number.isFinite(startColIdx)) return

    const touched = []
    rows.forEach((rowText, r) => {
      const rowEl = this.gridRowTargets[startRowIdx + r]
      if (!rowEl) return
      rowText.split("\t").forEach((value, c) => {
        const cell = rowEl.querySelector(`[data-portfolio-cash-flow-target="gridCell"][data-bucket-index="${startColIdx + c}"]`)
        if (!cell) return
        cell.value = value.trim()
        touched.push(cell)
      })
    })
    if (touched.length === 0) return

    const ok = await this._saveCells(touched, { source: "manual" })
    if (ok) this._track("Portfolio Cash Flow Grid Pasted", { cell_count: touched.length })
  }

  async _saveCells(cells, { source, sourceDetail = null, extraCells = [] } = {}) {
    const payload = {
      source: source,
      source_detail: sourceDetail,
      // The rendered grain/as-of/horizon ride along so line-managed
      // (adjustment) writes compute their month math on the exact
      // buckets the user is looking at.
      grain: this.element.dataset.pcfGrain,
      as_of: this.element.dataset.pcfAsOf,
      horizon_months: this.element.dataset.pcfHorizon,
      cells: cells.map(cell => ({
        project_id: parseInt(cell.dataset.projectId, 10),
        due_date: cell.dataset.dueDate,
        amount: cell.value.trim()
      })).concat(extraCells)
    }

    cells.forEach(cell => cell.classList.add("opacity-50"))
    const resp = await this._ajaxJSON("POST", this.cellsUrlValue, payload)
    cells.forEach(cell => cell.classList.remove("opacity-50"))

    if (!resp.ok) {
      cells.forEach(cell => { cell.value = cell.dataset.original || "" })
      this._showToast(resp.body?.error || resp.body?.errors?.join(", ") || "Couldn't save those cells", "error")
      return false
    }
    if (resp.body?.errors?.length) {
      this._showToast(resp.body.errors.join(", "), "error")
    }

    // A first write onto a budgetless project seeds its budget — reload
    // so the row gains its project link + accurate derived numbers.
    if ((resp.body?.seeded_budgets || 0) > 0) {
      this._flashAndReload("Cash flow saved")
      return true
    }

    cells.forEach(cell => this._markCellSaved(cell))
    this._recomputeGridTotals()
    return true
  }

  _markCellSaved(cell) {
    // Normalize whatever was typed to the grid's "$1,234.56" display.
    const raw = cell.value.trim()
    const numeric = this._parseNumber(raw)
    const value = raw === "" || numeric === 0 ? "" : this._formatMoney(numeric)
    cell.value = value
    cell.dataset.original = value
    cell.title = value === "" ? "" : "Updated by you · just now · typed edit"
    const isEntered = value !== "" && this._parseNumber(value) !== 0
    cell.classList.remove("text-brand", "font-medium", "text-gray-500", "italic", "text-gray-300")
    cell.parentElement?.classList.toggle("bg-brand/5", isEntered)
    if (isEntered) {
      cell.classList.add("text-brand", "font-medium")
    } else if (value === "") {
      cell.classList.add("text-gray-300")
    } else {
      cell.classList.add("text-gray-500", "italic")
    }
  }

  _recomputeGridTotals() {
    const columnSums = {}

    // Read-only rows keep server-rendered totals; only editable rows'
    // inputs are re-summed. Column totals therefore recompute from BOTH:
    // input values where present, otherwise the cell's rendered text.
    this.gridRowTargets.forEach(row => {
      let rowSum = 0
      let sawInput = false
      row.querySelectorAll("td").forEach(td => {
        const input = td.querySelector('[data-portfolio-cash-flow-target="gridCell"]')
        const col = (input || td).dataset.bucketIndex
        if (col === undefined) return
        const value = input ? this._parseNumber(input.value) : this._parseNumber(td.textContent)
        columnSums[col] = (columnSums[col] || 0) + value
        rowSum += value
        if (input) sawInput = true
      })
      if (!sawInput) return
      const totalCell = this.rowTotalTargets.find(t => t.dataset.projectId === row.dataset.projectId)
      if (totalCell) totalCell.textContent = this._formatMoney(rowSum)
    })

    let grand = 0
    this.columnTotalTargets.forEach(cell => {
      const sum = columnSums[cell.dataset.bucketIndex] || 0
      cell.textContent = this._formatMoney(sum)
      grand += sum
    })
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this._formatMoney(grand)
  }

  // ── AI import (item C) ─────────────────────────────────────────────

  showImportModal() {
    this._resetImportModal()
    this._show(this.importModalTarget)
  }

  hideImportModal() { this._hide(this.importModalTarget) }

  openImportFilePicker() { this.importFileInputTarget.click() }

  onImportDragOver(event) {
    event.preventDefault()
    this.importDropAreaTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  onImportDragLeave() {
    this.importDropAreaTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  onImportDrop(event) {
    event.preventDefault()
    this.onImportDragLeave()
    const file = event.dataTransfer?.files?.[0]
    if (file) this._uploadImportFile(file)
  }

  onImportFileChosen(event) {
    const file = event.currentTarget.files?.[0]
    if (file) this._uploadImportFile(file)
  }

  async _uploadImportFile(file) {
    this._importFilename = file.name
    this._hide(this.importDropzoneTarget)
    this._hide(this.importErrorTarget)
    this._show(this.importLoadingTarget)

    const form = new FormData()
    form.append("file", file)

    let resp
    try {
      const raw = await fetch(this.importUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
        body: form
      })
      resp = { ok: raw.ok, body: await raw.json().catch(() => null) }
    } catch (err) {
      resp = { ok: false, body: { error: err.message } }
    }

    this._hide(this.importLoadingTarget)
    if (!resp.ok) {
      this._showImportError(resp.body?.error || "We couldn't read that file.")
      return
    }

    this._importProjects = resp.body.projects || []
    this._importRows = resp.body.rows || []
    this._renderImportReview()
  }

  _renderImportReview() {
    const rows = this._importRows
    if (!rows.length) {
      this._showImportError("We couldn't find any project cash-flow rows in that file.")
      return
    }

    // Real ConsLog projects fill the list; placeholders a previous import
    // created are quarantined in their own group at the bottom (Phelipe
    // 2026-08-27 — the picker was showing "a bunch of random projects").
    // They stay selectable so a re-import can still land on the same one.
    const opt = p => `<option value="${p.id}" data-editable="${p.editable}">${this._escape(p.label)}</option>`
    const realProjects = this._importProjects.filter(p => !p.shell)
    const shellProjects = this._importProjects.filter(p => p.shell)
    const options = realProjects.map(opt).join("") +
      (shellProjects.length
        ? `<optgroup label="Cash-flow only (added by an import)">${shellProjects.map(opt).join("")}</optgroup>`
        : "")

    this.importRowsTarget.innerHTML = rows.map((row, i) => {
      const monthsLabel = row.month_count > 0
        ? `${row.month_count} <span class="text-gray-400">(${this._escape(row.first_month)} → ${this._escape(row.last_month)})</span>`
        : "0"
      const negNote = row.negative_count > 0
        ? `<div class="text-[11px] text-amber-700 mt-0.5">${row.negative_count} credit cell(s) skipped</div>`
        : ""
      const lineManaged = row.project_id && row.editable === false
      return `
        <tr data-import-row="${i}" class="${lineManaged ? "bg-amber-50/40" : ""}">
          <td class="px-2 py-1.5 text-gray-700">
            ${this._escape(row.label)}
            ${row.number ? `<div class="text-[11px] text-gray-400">${this._escape(row.number)}</div>` : ""}
            ${negNote}
          </td>
          <td class="px-2 py-1.5 min-w-[220px]">
            <select data-import-project
                    data-action="change->portfolio-cash-flow#importProjectChanged"
                    class="w-full text-[13px] border rounded px-1.5 py-1 bg-white cursor-pointer ${row.project_id ? "border-gray-300" : "border-amber-400 bg-amber-50"}">
              <option value="">— skip this row —</option>
              ${options}
            </select>
            <div data-import-link-note class="mt-0.5">${this._importLinkNote(!!row.project_id, row.label)}</div>
            ${lineManaged ? `<div class="text-[11px] text-amber-700 mt-0.5">Line-managed — edit on the project's own Cash Flow page</div>` : ""}
          </td>
          <td class="px-2 py-1.5 text-right text-gray-600 tabular-nums whitespace-nowrap">${monthsLabel}</td>
          <td class="px-2 py-1.5 text-right text-gray-800 tabular-nums">${this._formatMoney(row.total_usd)}</td>
          <td class="px-2 py-1.5 text-right">
            <input type="text" value="1.0" data-import-rate
                   class="w-16 text-right text-[13px] border border-gray-300 rounded px-1.5 py-1 tabular-nums">
          </td>
          <td class="px-2 py-1.5 text-center">
            <button type="button" data-action="click->portfolio-cash-flow#removeImportRow"
                    class="text-gray-300 hover:text-red-600 cursor-pointer" aria-label="Remove row">✕</button>
          </td>
        </tr>
      `
    }).join("")

    // Preselect the AI/number matches after injection.
    rows.forEach((row, i) => {
      if (!row.project_id) return
      const select = this.importRowsTarget.querySelector(`[data-import-row="${i}"] [data-import-project]`)
      if (select) select.value = String(row.project_id)
    })

    const matched = rows.filter(r => r.project_id).length
    const unmatched = rows.length - matched
    this.importSummaryTarget.innerHTML = unmatched > 0
      ? `Found <strong>${rows.length}</strong> projects in the file. <strong>${matched}</strong> matched; <strong>${unmatched}</strong> need you to pick a project (or stay skipped).`
      : `Found <strong>${rows.length}</strong> projects in the file, all matched.`

    this._show(this.importReviewTarget)
    this._show(this.importApplyButtonTarget)
  }

  // The caption under each dropdown has to tell the truth about what the row
  // will DO on apply (Phelipe 2026-08-27) — it used to render once from the
  // auto-match and then never change, so a row switched to "skip this row"
  // still claimed "linked to this project".
  _importLinkNote(linked, label, explicitSkip = false) {
    if (linked) {
      return `<span class="text-[11px] text-emerald-700 inline-flex items-center gap-1"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>linked to this project</span>`
    }
    if (explicitSkip) {
      return `<span class="text-[11px] text-gray-500">skipped — nothing imported for this row</span>`
    }
    // Imports never create projects (Phelipe 2026-08-31) — an unmatched row
    // is skipped unless a project is picked by hand.
    return `<span class="text-[11px] text-gray-400">no similar project — this row will be skipped unless you pick one</span>`
  }

  // Picking "— skip this row —" by hand is a real decision: the row is then
  // skipped outright, even with the cash-flow-only rule turned on.
  importProjectChanged(event) {
    const select = event.currentTarget
    const tr = select.closest("tr")
    const row = this._importRows[parseInt(tr?.dataset.importRow, 10)]
    const linked = select.value !== ""
    select.dataset.userChoice = "1"
    const note = tr?.querySelector("[data-import-link-note]")
    if (note) note.innerHTML = this._importLinkNote(linked, row?.label || "", !linked)
    select.classList.toggle("border-amber-400", !linked)
    select.classList.toggle("bg-amber-50", !linked)
    select.classList.toggle("border-gray-300", linked)
  }


  removeImportRow(event) {
    const tr = event.currentTarget.closest("tr")
    const idx = parseInt(tr?.dataset.importRow, 10)
    if (Number.isFinite(idx)) this._importRows[idx] = null
    tr?.remove()
    if (this.importRowsTarget.children.length === 0) {
      this._hide(this.importApplyButtonTarget)
      this._showImportError("No rows left to import.")
    }
  }

  async applyImport() {
    // Phase choice: standalone list — the rows go to their own table and the
    // page flips to the standalone grid. Projects are never touched.
    if (this.hasImportStandaloneCheckboxTarget && this.importStandaloneCheckboxTarget.checked) {
      return this._applyStandaloneImport()
    }

    const cells = []
    const overrideExisting = this.hasImportOverrideCheckboxTarget && this.importOverrideCheckboxTarget.checked

    this.importRowsTarget.querySelectorAll("tr").forEach(tr => {
      const idx = parseInt(tr.dataset.importRow, 10)
      const row = this._importRows[idx]
      if (!row) return

      let rate = parseFloat(tr.querySelector("[data-import-rate]")?.value)
      if (!Number.isFinite(rate) || rate <= 0) rate = 1.0

      const select = tr.querySelector("[data-import-project]")
      const projectId = parseInt(select?.value, 10)
      // Only rows linked to an existing project import — never invented ones
      // (Phelipe 2026-08-31): an import can't know a project's phase.
      if (!Number.isFinite(projectId)) return

      row.entries.forEach(entry => {
        cells.push({
          project_id: projectId,
          due_date: `${entry.month}-01`,
          amount: (Math.round(entry.usd * rate * 100) / 100).toFixed(2),
          usd_amount: entry.usd.toFixed(2),
          rate: rate
        })
      })
    })

    if (cells.length === 0) {
      this._showImportError("Pick a project for at least one row first — rows without a project are skipped.")
      return
    }

    this._setApplyLoading(true)
    const resp = await this._ajaxJSON("POST", this.cellsUrlValue, {
      source: "import",
      source_detail: this._importFilename,
      grain: this.element.dataset.pcfGrain,
      as_of: this.element.dataset.pcfAsOf,
      horizon_months: this.element.dataset.pcfHorizon,
      override_existing: overrideExisting,
      cells: cells
    })
    this._setApplyLoading(false)

    if (!resp.ok) {
      this._showImportError(resp.body?.error || resp.body?.errors?.join(", ") || "Couldn't apply the import.")
      return
    }

    this._track("Portfolio Cash Flow Import Applied", {
      cell_count: cells.length,
      saved_count: resp.body?.saved || 0,
      seeded_budget_count: resp.body?.seeded_budgets || 0
    })
    this._hide(this.importModalTarget)
    this._flashAndReload(`Imported ${resp.body?.saved || cells.length} monthly amounts`)
  }

  _resetImportModal() {
    this._importRows = []
    this._importProjects = []
    this._importFilename = null
    this.importRowsTarget.innerHTML = ""
    this.importFileInputTarget.value = ""
    this.importSummaryTarget.innerHTML = ""
    this._show(this.importDropzoneTarget)
    this._hide(this.importLoadingTarget)
    this._hide(this.importReviewTarget)
    this._hide(this.importErrorTarget)
    this._hide(this.importApplyButtonTarget)
    // Rules stay visible the whole time; both default ON per open.
    if (this.hasImportOverrideCheckboxTarget) this.importOverrideCheckboxTarget.checked = true
  }

  _showImportError(message) {
    this.importErrorTarget.textContent = message
    this._show(this.importErrorTarget)
    this._show(this.importDropzoneTarget)
  }

  // Spinner in the Apply button while the cells save (Phelipe
  // 2026-08-26) — a 60-project import takes a moment and the modal gave
  // no sign it was working. Success reloads the page; failure restores
  // the button so a retry is one click away.
  _setApplyLoading(on) {
    if (!this.hasImportApplyButtonTarget) return
    const btn = this.importApplyButtonTarget
    btn.disabled = on
    if (on) {
      btn.dataset.originalHtml = btn.innerHTML
      btn.innerHTML = '<svg class="animate-spin h-4 w-4 inline-block align-middle" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="align-middle" style="margin-left:6px">Applying…</span>'
    } else if (btn.dataset.originalHtml) {
      btn.innerHTML = btn.dataset.originalHtml
      delete btn.dataset.originalHtml
    }
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Shared helpers (same shapes as cash_flow_controller.js) ────────

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _parseNumber(raw) {
    const cleaned = String(raw ?? "").replace(/[^\d.\-]/g, "")
    const value = parseFloat(cleaned)
    return Number.isFinite(value) ? value : 0
  }

  _formatMoney(value) {
    return "$" + Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    })
  }

  // Modal / section visibility. These were being CALLED throughout the
  // import flow but never defined — `showImportModal` threw, which is
  // why Smart Import appeared to do nothing.
  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _showToast(message, type = "success") {
    const toast = document.createElement("div")
    toast.className = type === "success" ? "flash-toast-notice" : "flash-toast-alert"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  _flashAndReload(message) {
    sessionStorage.setItem("portfolio_cash_flow_flash", message)
    window.location.reload()
  }

  _showPendingFlash() {
    const message = sessionStorage.getItem("portfolio_cash_flow_flash")
    if (!message) return
    sessionStorage.removeItem("portfolio_cash_flow_flash")
    this._showToast(message)
  }

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, c => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  _track(event, props = {}) {
    try { mixpanelTrack(event, props) } catch { /* analytics must never break the page */ }
  }

  // ── Standalone list (phase choice, Phelipe 2026-08-11) ──────────────

  standaloneCheckboxChanged() {
    // The two project rules mean nothing for a standalone list.
    const on = this.importStandaloneCheckboxTarget.checked
    ;[this.importOverrideCheckboxTarget].forEach(box => {
      if (!box) return
      box.disabled = on
      box.closest("label")?.classList.toggle("opacity-40", on)
    })
  }

  async _applyStandaloneImport() {
    const rows = (this._importRows || [])
      .filter(row => (row.label || "").trim() !== "" && (row.entries || []).length)
      .map(row => ({
        label: row.label,
        number: row.number,
        entries: row.entries.map(e => ({ month: `${e.month}-01`, usd: e.usd }))
      }))

    if (rows.length === 0) {
      this._showImportError("Nothing to import — the file had no rows with amounts.")
      return
    }

    this._setApplyLoading(true)
    const resp = await this._ajaxJSON("POST", this.standaloneApplyUrlValue, {
      rows: rows, source_filename: this._importFilename
    })
    this._setApplyLoading(false)

    if (!resp.ok) {
      this._showImportError(resp.body?.error || "Couldn't create the standalone list.")
      return
    }
    // The page renders a different grid in standalone mode — reload into it.
    window.location.reload()
  }

  blurCell(event) { event.target.blur() }

  async saveStandaloneCell(event) {
    const cell = event.target
    const raw = cell.value.replace(/[$,\s]/g, "")
    const amount = raw === "" ? null : parseFloat(raw)
    if (raw !== "" && !Number.isFinite(amount)) {
      cell.value = this._formatStandalone(cell.dataset.original)
      return
    }
    const original = cell.dataset.original === "" ? null : parseFloat(cell.dataset.original)
    if ((amount ?? null) === (Number.isFinite(original) ? original : null)) {
      cell.value = this._formatStandalone(cell.dataset.original)
      return
    }

    cell.classList.add("opacity-50")
    const resp = await this._ajaxJSON("PATCH", this.standaloneCellsUrlValue, {
      cells: [{ row_id: cell.dataset.rowId, due_date: cell.dataset.dueDate, amount: amount }]
    })
    cell.classList.remove("opacity-50")

    if (!resp.ok || resp.body?.errors?.length) {
      cell.value = this._formatStandalone(cell.dataset.original)
      this._showToast(resp.body?.errors?.[0] || resp.body?.error || "Couldn't save that cell", "error")
      return
    }

    cell.dataset.original = amount ?? ""
    cell.value = this._formatStandalone(cell.dataset.original)
    cell.classList.toggle("text-gray-300", amount == null)
    cell.classList.toggle("text-gray-800", amount != null)
    this._recalcStandaloneTotals()
  }

  async addStandaloneRow(event) {
    event?.preventDefault?.()
    const label = this.standaloneNewRowInputTarget.value.trim()
    if (label === "") return

    const resp = await this._ajaxJSON("POST", this.standaloneRowsUrlValue, { label: label })
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Couldn't add that row", "error")
      return
    }
    window.location.reload()
  }

  // Two-step delete (no browser confirm): first click arms the ✕, second
  // click within 2.5s removes the row.
  async deleteStandaloneRow(event) {
    const btn = event.currentTarget
    if (!btn.dataset.armed) {
      btn.dataset.armed = "1"
      btn.classList.add("text-red-600")
      btn.title = "Click again to remove this row and its numbers"
      setTimeout(() => { delete btn.dataset.armed; btn.classList.remove("text-red-600") }, 2500)
      return
    }

    const resp = await this._ajaxJSON("DELETE", `${this.standaloneRowsUrlValue}/${btn.dataset.rowId}`, {})
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Couldn't remove that row", "error")
      return
    }
    document.querySelector(`tr[data-standalone-row-id="${btn.dataset.rowId}"]`)?.remove()
    this._recalcStandaloneTotals()
  }

  switchToLinked() { this._toggleStandalone(false) }
  switchToStandalone() { this._toggleStandalone(true) }

  async _toggleStandalone(active) {
    const resp = await this._ajaxJSON("POST", this.standaloneToggleUrlValue, { active: active })
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Couldn't switch views", "error")
      return
    }
    window.location.reload()
  }

  _formatStandalone(original) {
    if (original === "" || original == null) return ""
    const n = parseFloat(original)
    if (!Number.isFinite(n)) return ""
    return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  _recalcStandaloneTotals() {
    const cells = [...document.querySelectorAll("[data-standalone-cell]")]
    const money = n => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const val = cell => { const n = parseFloat(cell.dataset.original); return Number.isFinite(n) ? n : 0 }

    const rowTotals = {}
    const colTotals = {}
    let grand = 0
    cells.forEach(cell => {
      const v = val(cell)
      rowTotals[cell.dataset.rowId] = (rowTotals[cell.dataset.rowId] || 0) + v
      colTotals[cell.dataset.dueDate] = (colTotals[cell.dataset.dueDate] || 0) + v
      grand += v
    })
    document.querySelectorAll("[data-standalone-row-total]").forEach(td => {
      td.textContent = money(rowTotals[td.dataset.standaloneRowTotal] || 0)
    })
    document.querySelectorAll("[data-standalone-col-total]").forEach(td => {
      const v = colTotals[td.dataset.standaloneColTotal] || 0
      td.textContent = v === 0 ? "—" : money(v)
    })
    const grandTd = document.querySelector("[data-standalone-grand-total]")
    if (grandTd) grandTd.textContent = money(grand)
  }
}
